import { useEffect, useState } from "react";
import { CrmLayout } from "@/components/crm/CrmLayout";
import { api, apiError } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/crmConstants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Users() {
  const { user } = useAuth();
  const isAdmin = user.role === "administrator";
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "advisor" });
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/crm/users").then((r) => { setUsers(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/crm/users", form); toast.success("Utilisateur créé."); setOpen(false); setForm({ email: "", name: "", password: "", role: "advisor" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleActive = async (u) => { await api.patch(`/crm/users/${u.id}`, { active: !u.active }); load(); };

  if (loading) return <CrmLayout><div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-blue-600" /></div></CrmLayout>;

  return (
    <CrmLayout>
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-bold text-slate-900">Utilisateurs & permissions</h1><p className="text-slate-500 text-sm mt-1">Rôles : administrateur, responsable commercial, conseiller, lecture seule.</p></div>
        {isAdmin && <Button onClick={() => setOpen(true)} data-testid="add-user"><Plus size={16} className="mr-1" />Ajouter</Button>}
      </div>

      <Card className="mt-5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="px-4 py-3 font-medium">Nom</th><th className="px-4 py-3 font-medium">E-mail</th><th className="px-4 py-3 font-medium">Rôle</th><th className="px-4 py-3 font-medium">Statut</th>{isAdmin && <th className="px-4 py-3" />}</tr></thead>
          <tbody>{users.map((u) => (
            <tr key={u.id} className="border-t border-slate-100">
              <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
              <td className="px-4 py-3 text-slate-600">{u.email}</td>
              <td className="px-4 py-3"><Badge variant="secondary">{ROLE_LABELS[u.role] || u.role}</Badge></td>
              <td className="px-4 py-3">{u.active === false ? <Badge className="bg-rose-500">Inactif</Badge> : <Badge className="bg-emerald-600">Actif</Badge>}</td>
              {isAdmin && <td className="px-4 py-3 text-right">{u.role !== "administrator" && <Button size="sm" variant="outline" onClick={() => toggleActive(u)}>{u.active === false ? "Activer" : "Désactiver"}</Button>}</td>}
            </tr>))}</tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvel utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="mb-1.5 block">Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
            <div><Label className="mb-1.5 block">E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
            <div><Label className="mb-1.5 block">Mot de passe</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
            <div><Label className="mb-1.5 block">Rôle</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="administrator">Administrateur</SelectItem><SelectItem value="sales_manager">Responsable commercial</SelectItem><SelectItem value="advisor">Conseiller</SelectItem><SelectItem value="read_only">Lecture seule</SelectItem></SelectContent></Select></div>
          </div>
          <DialogFooter><Button onClick={create} data-testid="save-user">Créer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </CrmLayout>
  );
}
